// @mui material components
import React from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Senthil Solar React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Senthil Solar React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Icons
import HomeIcon from "@mui/icons-material/Home";
import BoltIcon from "@mui/icons-material/Bolt";
import SavingsIcon from "@mui/icons-material/Savings";
import SolarPowerIcon from "@mui/icons-material/SolarPower";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Images - Use an existing image or add 'residential_bg.jpg' to src/assets/images/
// For now, reusing 'canva_banner.png' from Awards as a placeholder; replace with a residential themed bg
import bgImage from "assets/images/canva_banner.png";

// Animation imports
import { useSpring, animated, useTrail } from 'react-spring';
import { useInView } from 'react-intersection-observer';

// Create animated versions of MUI components
const AnimatedGrid = animated(Grid);
const AnimatedMKBox = animated(MKBox);

function ResidentialService() {
  // For header animation
  const [headerRef, headerInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });
  
  const headerAnimation = useSpring({
    opacity: headerInView ? 1 : 0,
    transform: headerInView ? 'translateY(0)' : 'translateY(-50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  // For why choose section
  const [whyRef, whyInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });

  const whyHeaderAnimation = useSpring({
    opacity: whyInView ? 1 : 0,
    transform: whyInView ? 'translateY(0)' : 'translateY(30px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const benefits = [
    {
      icon: <BoltIcon fontSize="large" />,
      title: "Reduce Electricity Bills",
      description: "Cut your monthly electricity costs by up to 90% with our efficient solar panel systems."
    },
    {
      icon: <SavingsIcon fontSize="large" />,
      title: "High ROI",
      description: "Recover your investment in 3-5 years and enjoy free electricity for 25+ years."
    },
    {
      icon: <SolarPowerIcon fontSize="large" />,
      title: "Environmental Impact",
      description: "Reduce your carbon footprint and contribute to a cleaner, greener future."
    },
    {
      icon: <HomeIcon fontSize="large" />,
      title: "Increase Property Value",
      description: "Homes with solar installations have higher resale values and attract eco-conscious buyers."
    }
  ];

  const [benefitsRef, benefitsInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const benefitsTrail = useTrail(benefits.length, {
    opacity: benefitsInView ? 1 : 0,
    transform: benefitsInView ? 'translateY(0) scale(1)' : 'translateY(50px) scale(0.95)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For installation process section
  const [installRef, installInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const installAnimation = useSpring({
    opacity: installInView ? 1 : 0,
    transform: installInView ? 'translateX(0)' : 'translateX(-50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  // For system specs section
  const [specsRef, specsInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const specsAnimation = useSpring({
    opacity: specsInView ? 1 : 0,
    transform: specsInView ? 'translateX(0)' : 'translateX(50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  return (
    <>
      <DefaultNavbar
        routes={routes}
        transparent
        light
      />
      <MKBox
        minHeight="60vh"
        width="100%"
        sx={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container sx={{mt:6}}>
          <AnimatedGrid
            ref={headerRef}
            style={headerAnimation}
            container
            item
            xs={12}
            lg={7}
            justifyContent="center"
            mx="auto"
          >
            <MKTypography
              variant="h1"
              color="white"
              mt={-6}
              mb={1}
              sx={({ breakpoints, typography: { size } }) => ({
                textAlign: "center",
                // Responsive font sizes
                [breakpoints.down("sm")]: {
                  fontSize: size["xl"],   // very small screens
                },
                [breakpoints.between("sm", "md")]: {
                  fontSize: size["2xl"],  // tablets
                },
                [breakpoints.up("md")]: {
                  fontSize: size["3xl"],  // desktops
                },
              })}
            >
              Residential Rooftop
              <br />
              Solar Systems
            </MKTypography>

            <MKTypography
              variant="h5"
              color="white"
              textAlign="center"
              sx={({ breakpoints, typography: { size } }) => ({
                opacity: 0.9,
                [breakpoints.down("sm")]: {
                  fontSize: size["md"],   // smaller subtitle on phones
                },
                [breakpoints.between("sm", "md")]: {
                  fontSize: size["lg"],   // tablets
                },
                [breakpoints.up("md")]: {
                  fontSize: size["xl"],   // desktops
                },
              })}
            >
              Transform your home into a clean energy powerhouse with our customized residential solar solutions
            </MKTypography>
          </AnimatedGrid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={6}>
          <Container>
            <AnimatedGrid 
              ref={whyRef}
              style={whyHeaderAnimation}
              container 
              item 
              xs={12} 
              lg={10} 
              justifyContent="center" 
              mx="auto" 
              mb={4}
            >
              <MKTypography variant="h4" textAlign="center" mb={3}>
                Why Choose Solar for Your Home?
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={3} ref={benefitsRef}>
              {benefitsTrail.map((style, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <AnimatedMKBox 
                    style={style}
                    display="flex" 
                    alignItems="flex-start" 
                    p={3} 
                    borderRadius="lg"
                    sx={{ 
                      backgroundColor: "rgba(0, 0, 0, 0.02)",
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-5px)"
                      }
                    }}
                  >
                    <MKBox color="info.main" mr={2}>
                      {React.cloneElement(benefits[index].icon, { fontSize: "large" })}
                    </MKBox>
                    <MKBox>
                      <MKTypography variant="h5" mb={1}>
                        {benefits[index].title}
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        {benefits[index].description}
                      </MKTypography>
                    </MKBox>
                  </AnimatedMKBox>
                </Grid>
              ))}
            </Grid>

            {/* Our Installation Process Section */}
            <AnimatedMKBox 
              ref={installRef}
              style={installAnimation}
              component="section" 
              mb={6}
              mt={6}
            >
              <MKTypography variant="h4" mb={3}>
                Our Installation Process
              </MKTypography>
              <MKTypography variant="body1" color="text">
                We follow a comprehensive approach to ensure seamless installation and optimal performance. 
                Our process includes site assessment, custom system design, government subsidy assistance, 
                professional installation, and post-installation support. Each residential project is backed 
                by our 25-year performance warranty and comprehensive maintenance services.
              </MKTypography>
            </AnimatedMKBox>

            {/* System Specifications Section */}
            <AnimatedMKBox 
              ref={specsRef}
              style={specsAnimation}
              component="section" 
              mb={6}
            >
              <MKTypography variant="h4" mb={3}>
                System Specifications
              </MKTypography>
              <MKTypography variant="body1" color="text">
                Our residential systems range from 1kW to 10kW capacity, suitable for homes of all sizes. 
                We use premium Tier-1 solar panels with efficiency ratings above 20%, paired with advanced 
                inverters for maximum power conversion. All installations comply with MNRE guidelines and 
                are eligible for government subsidies of up to 40% for systems up to 3kW.
              </MKTypography>
            </AnimatedMKBox>

          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default ResidentialService;